import { EventEmitter, Injectable, inject, signal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { IgniteResponse, SsoCallBackEventType } from '@clubhub/ignite/models';
import { FormlyFormOptions } from '@ngx-formly/core';
import {
  AffordabilityResult,
  CreateLenderAuditLogRequest,
  CriteriaResponse,
  CriteriaType,
  IgnitePlusAffordabilityCallbackRequest,
  LenderAuditResult,
  LenderAuditType,
  LendersAbilityResponse,
  LendingTypeCode,
  MatchedLenderProducts,
  MiSaveOutcomeModel,
  MortgageType,
  OutcomeDetails,
  OutcomeDetailsCard,
  OutcomeHistoryDetailsCard,
  OutcomeResponse,
  OutcomeResults, ProductCollectionType,
  ProductType,
  ProductTypeString,
  ProductsBdgModelRequest,
  ProductsBtlModelRequest,
  ProductsResidentialModelRequest,
  RepaymentMethod,
  SearchType,
  repaymentMethodToString,
} from '@shared/models';
import orderBy from 'lodash-es/orderBy';
import { Observable, Subject, combineLatest, firstValueFrom, forkJoin, of, timer } from 'rxjs';
import { catchError, defaultIfEmpty, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { v4 as newGuid } from 'uuid';

import { affordabilityMatched, igniteSortOptions, outcomeList, productsMatched } from '@clubhub/ignite/constants/ignite';
import {
  AffordabilityAndUiRequest,
  AffordabilityLenders,
  AffordabilityPollResponse,
  AffordabilityRequest,
  AnswerType,
  AuditResultsStatus,
  BdgAffordabilityRequest,
  BtlAffordabilityRequest,
  IApplicantOutcomeIds,
  IBaseIncomeContext,
  IIncomeAndExpenditure,
  IPropertyAndLoan,
  IPropertyBtl,
  IUiAffordabilityModel,
  IUiApplicant,
  IUiApplicantAge,
  IUiBaseIncome,
  IUiCriteria,
  IUiCriteriaQuestion,
  IUiMainIncome,
  IUiOtherMortgage,
  MainIncome,
  ResultsView,
  StepName,
  UiAdditionalIncome,
  benefitIncomeCriteria,
  otherIncomeCriteria,
  workRelatedIncomeCriteria,
} from '@clubhub/ignite/models/affordability';
import { OutcomeHistoryResponse } from '@clubhub/ignite/models/outcome-history';
import { PropertyModelRequest } from '@clubhub/ignite/models/property';
import { IntuitiveCriteriaPropertyStatus } from '@clubhub/ignite/pages/criteria-v2/models';
import { ProductPreferencesApplierService } from '@clubhub/ignite/services/filter-preferences/product-preferences-applier.service';
import { SearchFilterPreferencesService } from '@clubhub/ignite/services/filter-preferences/search-filter-preferences.service';
import { ProductsResultsService } from '@clubhub/ignite/services/products/products-results.service';
import { mockAffordabilityResponse } from '@clubhub/ignite/testing/affordability/mock-affordability-response';
import { IBasketItem, IContext } from '@clubhub/models';
import { BasketService } from '@clubhub/services';
import { roles } from '@shared/constants';
import { appName } from '@shared/constants/app-name';
import { ISortOption } from '@shared/models/filter-sort';
import { LenderAuditService } from '@shared/services';
import { AnalyticsService } from '@shared/services/analytics.service';
import { AuthorizeService } from '@shared/services/authorize.service';
import { ClubHubDataService } from '@shared/services/clubhub-data.service';
import { FormStateService } from '@shared/services/form-state.service';
import { IgniteHelperService } from '@shared/services/ignite.helper';
import { LendingTypeService } from '@shared/services/lending-type.service';
import { ModalService } from '@shared/services/modal.service';
import isEmpty from 'lodash-es/isEmpty';
import { AffordabilityService } from './affordability/affordability.service';
import { RioService } from './affordability/rio.service';
import { CriteriaV2Service } from './criteria/criteria-v2.service';
import { IgniteBtlService } from './ignite-btl.service';
import { IgniteCommonDataService } from './ignite.store';
import { ProductsFilterHelper } from './products/products-filter';
import { ProductsFilterService } from './products/products-filter.service';
import { ProductsSearchService } from './products/products-search.service';
import { PropertySearchService } from './property/property-search.service';
import { S365Service } from './s365.service';
import { SavedSearchCommonDataService } from './saved-searches/saved-search-common-data.service';

// Minimum time the affordability loading banner displays for (in milliseconds)
const minimumLoadingBannerTime = 6_000;

// Maximum time to wait for affordability responses before treating them as timeouts.
const maximumAffordabilityLoadingTime = 40_000;

type AffordabilityRequestUnion = AffordabilityRequest | BtlAffordabilityRequest | BdgAffordabilityRequest;

@Injectable({
  providedIn: 'root',
})
export class IgniteService {
  private readonly helperService = inject(IgniteHelperService);
  private readonly clubHubDataService = inject(ClubHubDataService);
  private readonly authService = inject(AuthorizeService);
  private readonly searchFilterPreferencesService = inject(SearchFilterPreferencesService);
  private readonly productPreferencesApplier = inject(ProductPreferencesApplierService);
  private readonly modalService = inject(ModalService);
  private readonly lenderAuditService = inject(LenderAuditService);
  private readonly lendingTypeService = inject(LendingTypeService);
  private readonly basketService = inject(BasketService);
  private readonly formStateService = inject(FormStateService);
  private readonly criteriaV2Service = inject(CriteriaV2Service);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly rioService = inject(RioService);
  private readonly productFilterService = inject(ProductsFilterService);
  private readonly productsFilterHelper = inject(ProductsFilterHelper);
  public readonly productsSearchService = inject(ProductsSearchService);
  private readonly igniteCommonDataService = inject(IgniteCommonDataService);
  private readonly s365Service = inject(S365Service);
  private readonly productsResultsService = inject(ProductsResultsService);
  private readonly igniteBtlService = inject(IgniteBtlService);
  private readonly savedSearchCommonDataService = inject(SavedSearchCommonDataService);
  private readonly affordabilityService = inject(AffordabilityService);
  private readonly propertySearchService = inject(PropertySearchService);

  public shouldShowLoadingOnSubmit = false;
  public activeStepIndex: number = 0;
  public auditResultsStatus: AuditResultsStatus = AuditResultsStatus.NotSubmitted;
  public caseEditedAll = false;
  public caseReran = false;
  private readonly _currentStepName = signal<StepName | undefined>(undefined);
  public drawerOpen = false;
  public form = new UntypedFormGroup({});
  public lenderCount?: number | null = null;
  public matchedLenderProducts: MatchedLenderProducts[] = [];
  public miSaveOutcomeModel: MiSaveOutcomeModel | null = null;
  private readonly mockAffordability = false;
  public model: IUiAffordabilityModel | undefined;
  public options = {} as FormlyFormOptions;
  public previousLendingTypeCode: LendingTypeCode | undefined;
  public productLendersList: string[] = [];
  public progress = 0;
  public scheduled = false;
  public skipListChange = new Subject<string[]>();
  public outcomeList: ISortOption[] = outcomeList;
  public affordabilityMatched: ISortOption[] = affordabilityMatched;
  public igniteSortOptions: ISortOption[] = igniteSortOptions;
  public productsMatched: ISortOption[] = productsMatched;
  public fixedInitialPeriod: number | undefined;
  public advertBannerVisible = true;
  public printSortOption: unknown;
  public savedReport: boolean = false;

  public get currentStepName(): StepName | undefined {
    return this._currentStepName();
  }

  public set currentStepName(value: StepName | undefined) {
    this._currentStepName.set(value);
  }

  public constructor() {
    this.getAvailableLenders();
    this.reset();

    // MCONE-6289 - fix product criteria modal not showing property criteria added to a products search
    this.criteriaV2Service.outcomeUpdated?.subscribe(outcome => {
      this.propertySearchService.splitOutPropertyCriteria(null, outcome.details);
    });
  }

  public get resultsLoading(): Observable<boolean> {
    return combineLatest([
      this.affordabilityService.loading$,
      this.criteriaV2Service.loading$,
      this.productsSearchService.anyLoading$,
    ]).pipe(
      map(([affordabilityLoading, criteriaLoading, anyProductsLoading]) => {
        // Only show loading if form should show loading AND there's actual loading happening
        return this.shouldShowLoadingOnSubmit && (affordabilityLoading || criteriaLoading || anyProductsLoading);
      }),
    );
  }

  public get productsAndAffordabilityLoading(): boolean {
    return (this.productsSearchService.anyLoading || this.criteriaV2Service.loading) && this.lendingTypeService.isBtlLendingType;
  }

  public get isInaccuracyOrBrokerEmail() {
    return !this.savedReport && this.authService.historyView;
  }

  public get lendingTypeCode() {
    if (this.previousLendingTypeCode !== (this.lendingTypeService.lendingType?.code as LendingTypeCode)) {
      this.previousLendingTypeCode = this.lendingTypeService.lendingType?.code as LendingTypeCode;
      this.getAvailableLenders();
    }
    return this.lendingTypeService.lendingType?.code as LendingTypeCode;
  }

  public get lendingTypeName() {
    return this.lendingTypeService.lendingType?.name ?? '';
  }

  public get hasAdditionalCriteria() {
    return this.criteriaV2Service.additionalOutcomes.length > 0 || this.criteriaV2Service.basketItems.length > 0;
  }

  public get hasAnyCriteria(): boolean {
    return !!this.criteriaV2Service.basketItems.length;
  }

  public get affordabilityLendersList() {
    return this.igniteCommonDataService.affordabilityLendersList;
  }

  public get hasAdditionalProperty() {
    return this.propertySearchService.propertyOutcomes.length > 0;
  }

  public addApplicant() {
    this.model?.applicants?.applicantAges?.push(this.initialApplicantAge);
    this.model?.applicants?.applicantAges?.forEach((applicant, index) => (applicant.applicantNumber = index + 1));
  }

  public addApplicantIncome() {
    if (this.model !== undefined && this.model?.incomeAndExpenditure === undefined) {
      const numOfApplicants = this.model?.applicants?.applicantAges?.length ?? 0;

      for (let i = 0; i <= numOfApplicants; i++) {
        const applicant = [] as IUiApplicant[];
        applicant.push({
          applicantNumber: i + 1,
        } as IUiApplicant);

        this.model.incomeAndExpenditure = {
          applicants: applicant,
        } as unknown as IIncomeAndExpenditure;
      }
    }

    this.model?.incomeAndExpenditure?.applicants?.push(this.initialApplicant);
    this.model?.incomeAndExpenditure?.applicants?.forEach((applicant, index) => (applicant.applicantNumber = index + 1));
  }

  public addBtlProperty() {
    this.model?.otherProperties?.propertiesBtl.push(this.initialPropertyBtl);
    this.model?.otherProperties?.propertiesBtl.forEach((applicant, index) => (applicant.propertyNumber = index + 1));
  }

  public addResiProperty() {
    this.model?.otherProperties?.properties.push(this.initialPropertyResi);
    this.model?.otherProperties?.properties.forEach((applicant, index) => (applicant.propertyNumber = index + 1));
  }

  public updateModel(model = {}) {
    if (this.model) {
      this.model = { ...this.model, ...model };
    }
  }

  public resetResults() {
    this.progress = 0;
    this.lenderCount = null;
    this.igniteCommonDataService.results = null;
    this.clearOutcomeState();

    this.clearPropertyOutcomeIdsWhenNotPreserved();
    this.propertySearchService.preservePropertyCriteria = false;
    this.productsSearchService.isOutcomeResults = false;

    this.criteriaV2Service.reset();
  }

  public resetParamsToDefault(): void {
    this.cancelAffordabilityPolling();
    this.productsSearchService.resetParamsToDefault();
    this.productsSearchService.reset();
    this.igniteCommonDataService.affordabilityModel = null;
    this.activeStepIndex = 0;
    this.criteriaV2Service.additionalOutcomeIds = [];
    this.affordabilityService.affordabilityModelRequest = null;
    this.auditResultsStatus = AuditResultsStatus.NotSubmitted;
    this.caseEditedAll = false;
    this.caseReran = false;
    this.drawerOpen = false;
    this.affordabilityService.lastSavedAffordabilitySearchId = '';
    this.igniteCommonDataService.lenders = [];
    this.affordabilityService.loading = false;
    this.criteriaV2Service.loading = false;
    this.productsSearchService.loading = false;
    this.productsSearchService.allLoading = false;
    this.progress = 0;
    this.criteriaV2Service.criteriaSearchId = null;
    this.propertySearchService.propertySearchId = null;
    this.propertySearchService.propertySearchRequestViewModel = null;
    this.igniteCommonDataService.resultsView = ResultsView.Details;
    this.scheduled = false;
    this.skipListChange = new Subject<string[]>();
    this.fixedInitialPeriod = 0;
    this.advertBannerVisible = true;
    this.propertySearchService.hasPresetPropertyValue = false;
    this.clearOutcomeState();
    this.shouldShowLoadingOnSubmit = false;
    this.clearPropertyOutcomeIdsWhenNotPreserved();
  }

  private clearOutcomeState(): void {
    this.criteriaV2Service.intuitiveContextCards = [];
    this.criteriaV2Service.intuitiveOutcomes = [];
    this.criteriaV2Service.additionalOutcomes = [];
    this.miSaveOutcomeModel = null;
    this.matchedLenderProducts = [];
    this.propertySearchService.propertyOutcomes = [];
  }

  private clearPropertyOutcomeIdsWhenNotPreserved(): void {
    if (!this.propertySearchService.preservePropertyCriteria && !this.igniteCommonDataService.propertyOnly) {
      this.propertySearchService.propertyOutcomeIds = [];
      this.propertySearchService.autoAddedPropertyOutcomeIds = [];
    }
  }

  public setOutcomeResult(isOutcomeResult: boolean) {
    this.productsSearchService.isOutcomeResults = isOutcomeResult;
  }

  public getAvailableLenders() {
    this.getAvailableLendersResponse().subscribe(lendersAbility => {
      this.igniteCommonDataService.affordabilityLendersList = lendersAbility.affordabilityLenders;
      this.productLendersList = lendersAbility.productLenders;
    });
  }

  public getAvailableLendersResponse() {
    return this.isBtlLendingType
      ? this.igniteBtlService.getAvailableLendersResponse()
      : this.clubHubDataService.get<LendersAbilityResponse>('lender/availableLenders?lendingTypeCode=RES');
  }

  private updateCriteriaBasket(contextCards: OutcomeHistoryDetailsCard[], autoAdded = false) {
    contextCards.forEach(o => {
      const issue = o.issue;
      const context = o.context;
      if (!issue || !context) {
        return;
      }

      if (!this.basketService.basketItems.find((x: IBasketItem) => x.issue?.id === issue.id)) {
        this.basketService.addBasketItem(issue);
      }
      const propertyIssue = this.basketService.basketItems.find((x: IBasketItem) => x.issue?.id === issue.id);
      if (!propertyIssue?.contexts.find((x: IContext) => x.context.id === context.id)) {
        this.basketService.addQuestionAndAnswerMulti(issue.id, context, o.questionsAndAnswers, autoAdded ? autoAdded : o.autoAdded);
      }
    });
  }

  /**
   * Whether the broker's saved preferences have already been applied to the affordability results filters on
   * this search.
   *
   * The outcomes screen is re-created whenever the broker edits the search or adds a tool, and it used to reset
   * the filters every time - so any change they had made was lost. Preferences now apply once, on the first
   * showing, and `reset` (New search, the Ignite landing page, loading a saved search) re-arms it.
   */
  public affordabilityFilterPreferencesApplied = false;

  public reset(resultModel: AffordabilityResult | null = null) {
    this.affordabilityFilterPreferencesApplied = false;
    this.cancelAffordabilityPolling();
    this.form.markAsUntouched();
    this.resetResults();
    this.affordabilityService.affordabilityModelRequest = null;
    this.authService.historyView = false;
    this.resetOptions();
    this.savedReport = false;
    this.igniteCommonDataService.searchToolsInUse = {
      affordability: false,
      products: false,
      property: false,
      criteria: false,
    };
    this.model = this.initialModel();
    this.rioService.initializeRioForm();
    this.form = new UntypedFormGroup({});
    this.options = {};
    this.shouldShowLoadingOnSubmit = false;
    this.propertySearchService.propertySearchRequestViewModel = null;

    this.igniteCommonDataService.affordabilityModel = this.model;

    if (resultModel) {
      if (resultModel.uiModelJson) {
        const restoredModel = JSON.parse(resultModel.uiModelJson) as IUiAffordabilityModel;
        this.renumberApplicants(restoredModel);
        this.applyUiModel(restoredModel);
      }

      if (resultModel?.intuitiveOutcomesContextCards?.length) {
        const cards: OutcomeDetailsCard[] = [];
        resultModel.intuitiveOutcomesContextCards.forEach(card => {
          cards.push({
            answerId: card.answerId,
            context: card.context?.text ?? '',
            issue: `${card.issue?.text ?? ''} - Intuitive Criteria`,
            questionsAndAnswers: card.questionsAndAnswers.map(cq => {
              return {
                question: cq.question.text,
                answer: cq.answer.text,
                answerType: cq.answerType,
              };
            }),
          });
        });
        this.criteriaV2Service.intuitiveContextCards = cards;

        this.criteriaV2Service.outcome.contexts = cards;
        this.criteriaV2Service.setBasketItems(cards);
      }
      if (resultModel?.outcomeContextCards?.length) {
        this.criteriaV2Service.outcome.details = [...resultModel.additionalCriteria, ...resultModel.intuitiveCriteria];

        const cards: OutcomeDetailsCard[] = [];
        resultModel.outcomeContextCards.forEach(card => {
          cards.push(OutcomeHistoryDetailsCard.card(card, card.autoAdded ? ' - Added Automatically' : ''));
        });

        this.criteriaV2Service.outcome.contexts = [...cards, ...(this.criteriaV2Service.outcome?.contexts ?? [])];
        this.criteriaV2Service.setBasketItems(this.criteriaV2Service.outcome.contexts);

        this.updateCriteriaBasket([...resultModel.outcomeContextCards]);
      }

      this.productsSearchService.matchedProductsFlat = resultModel.products
        .flatMap(p => p.products ?? [])
        .filter(product => !!product.initialPeriod);

      this.matchedLenderProducts = resultModel.products;
      this.productsSearchService.matchedProducts = resultModel.products;

      this.igniteCommonDataService.results = {
        pollJobID: null,
        quotes: resultModel.affordabilityResults,
        affordabilitySearchId: resultModel.affordabilityResults[0]?.affordabilitySearchId ?? null,
        errorMessage: null,
        completed: true,
        lenderCount: resultModel.affordabilityResults.length,
      };
      this.igniteCommonDataService.lenders = resultModel.affordabilityResults.map(r => ({
        lenderName: r.lenderName,
        lenderLogoUrl: r.lenderLogoUrl ?? '',
      }));

      this.propertySearchService.splitOutPropertyCriteria(resultModel.intuitiveCriteria, resultModel.additionalCriteria);

      this.currentStepName = StepName.Results;
      this.igniteCommonDataService.resultsView = ResultsView.Details;
      this.affordabilityService.lastSavedAffordabilitySearchId = null;
      this.miSaveOutcomeModel = resultModel.saveOutcomeModel;
    }
  }

  public resetOptions(): void {
    if (this.options.resetModel) {
      this.options.resetModel();
    }
  }

  public get productSearchId() {
    return this.igniteCommonDataService.affordabilitySearchId ?? this.productsSearchService.productsSearchId;
  }

  public get productsModelRequest() {
    return this.productsSearchService.productsModelRequest;
  }

  public get currentAppliedFilters() {
    return this.productFilterService.currentAppliedFilters;
  }

  public get isBridgingType(): boolean {
    return this.lendingTypeCode === LendingTypeCode.Bdg.toLowerCase();
  }

  public get productType(): ProductCollectionType {
    return this.isBridgingType ? ProductCollectionType.Bridging : ProductCollectionType.ResiBtl;
  }

  public get activeTab(): SearchType {
    return this.igniteCommonDataService.source ?? SearchType.Affordability;
  }

  public getMiProductsDataModel() {
    const model = {
      criteriaSearchId: this.criteriaV2Service.criteriaSearchId ?? this.productsSearchService.productsIntuitiveCriteriaId,
      affordabilitySearchId: this.igniteCommonDataService.affordabilitySearchId,
      propertySearchId: this.propertySearchService.propertySearchId,
      productsSearchId: this.productSearchId,
      lenderAffordabilityData: this.getLenderAffordabilityData(),
      products: this.productsSearchService.miProductsResult,
      productsSearchRequest: this.productsModelRequest,
      productsFilters: this.currentAppliedFilters,
      productResultsConfiguration: this.productsResultsService.getCurrentResultConfigurationRequestModel(this.lendingTypeCode, true),
      lendersWithMatchedProducts: this.getLendersWithMatchedProducts(),
      source: this.activeTab,
    } satisfies Partial<MiSaveOutcomeModel>;

    return model;
  }

  public saveMiProductsSearchDetails() {
    this.clubHubDataService.post('Product/SaveMiProductsSearchDetails', this.getMiProductsDataModel()).subscribe();
  }

  public getOutcomeItem(outcome: OutcomeResults): ISortOption | undefined {
    return this.outcomeList.find(o => outcome === o.value);
  }

  public getResults(): IgniteResponse[] | null {
    if (!this.igniteCommonDataService.results?.quotes) {
      return null;
    }

    return orderBy(
      this.igniteCommonDataService.results.quotes,
      [
        x => this.isAffordabilityLender(x.lenderName),
        x => this.isErrorResult(x),
        x => this.isLoadingResult(x),
        x => x.maximumAffordableLoanAmount ?? -1,
        x => x.lenderName,
      ],
      ['desc', 'asc', 'asc', 'desc', 'asc'],
    );
  }

  public isAffordabilityLender(lenderName: string) {
    return this.affordabilityLendersList?.includes(lenderName);
  }

  public isLoadingResult(result: IgniteResponse): boolean {
    return this.affordabilityService.affordabilityLoading$.value && !result.affordabilitySearchId && !this.isErrorResult(result);
  }

  public isErrorResult(result: IgniteResponse): boolean {
    return (
      !!result.affordabilitySearchId?.length &&
      (!!result.httpError || !!result.error?.message) &&
      !this.igniteCommonDataService.isNetFreePayTooLow(result)
    );
  }

  public getIntuitiveOutcome(lenderName: string, intuitiveOutcomes: OutcomeResponse[] | null = null): OutcomeResponse | undefined {
    if (intuitiveOutcomes) {
      return intuitiveOutcomes.find((o: OutcomeResponse) => o.overallResult.lender.toUpperCase() === lenderName.toUpperCase());
    }

    return this.criteriaV2Service.getLenderCriteriaByLender(
      lenderName,
      CriteriaType.Intuitive,
      IntuitiveCriteriaPropertyStatus.NoProperty,
    );
  }

  public getPropertyIntuitiveOutcome(lenderName: string): OutcomeResponse | undefined {
    return this.criteriaV2Service.getLenderCriteriaByLender(
      lenderName.toUpperCase(),
      CriteriaType.Intuitive,
      IntuitiveCriteriaPropertyStatus.Property,
    );
  }

  public getAdditionalOutcome(lenderName: string, additionalOutcomes: OutcomeResponse[] | null = null): OutcomeResponse | undefined {
    if (additionalOutcomes) {
      return additionalOutcomes.find((o: OutcomeResponse) => o.overallResult.lender.toUpperCase() === lenderName.toUpperCase());
    }

    return this.criteriaV2Service.getLenderCriteriaByLender(
      lenderName,
      CriteriaType.Manual,
      IntuitiveCriteriaPropertyStatus.NoProperty,
    );
  }

  public getPropertyOutcome(lenderName: string): OutcomeResponse | undefined {
    return this.propertySearchService.propertyOutcomes.find(
      (o: OutcomeResponse) => o.overallResult.lender.toUpperCase() === lenderName.toUpperCase(),
    );
  }

  public getLenderMatchedProducts(response: IgniteResponse): MatchedLenderProducts | null {
    const matchedLenderProducts: MatchedLenderProducts | undefined = this.matchedLenderProducts.find(
      (o: MatchedLenderProducts) => o.lenderName.toUpperCase() === response.lenderName.toUpperCase(),
    );
    if (matchedLenderProducts === undefined) {
      return null;
    }

    // When doing a BTL products-only search, the user may fill enough out to have affordability get automatically added
    // as part of the search. If this happens, the affordability page will need to show matched products, however to
    // support the real-time product rate type and initial period filters, the products endpoint will have returned all
    // products. Therefore, we need to test against these filters again here to determine if there are actually any
    // matched products.
    if (this.lendingTypeCode === LendingTypeCode.Btl && this.igniteCommonDataService.productsOnly && this.model) {
      const productLength = this.model?.propertyAndLoan?.productLength;
      const productTypeExtended = this.model?.propertyAndLoan?.productTypeExtended;
      const productTypeFilter =
        productTypeExtended !== undefined ? this.productsFilterHelper.productTypeFilters.get(productTypeExtended) : undefined;
      // Don't need to worry about 'For Term' length, because that's not allowed for affordability, only 1-5 years are.
      const [lowerPeriod, upperPeriod] = this.productsFilterHelper.getProductLengthBounds(productLength ?? 0);

      return {
        ...matchedLenderProducts,
        products: matchedLenderProducts.products
          ?.filter(productTypeFilter ?? (() => true))
          .filter(p => p.initialPeriod && p.initialPeriod >= lowerPeriod && p.initialPeriod <= upperPeriod),
      };
    }

    // For resi, the only time the user would see affordability results is when doing an affordability search, at which
    // point the products filters are disabled and the response from the API will ONLY contain matched products. So we
    // can skip the additional filters and just return all the lender's products that were in the response from the API.
    return matchedLenderProducts;
  }

  public hasLenderMatchedProducts(response: IgniteResponse): boolean {
    const lenderMatchedProducts = this.getLenderMatchedProducts(response)?.products;
    if (lenderMatchedProducts?.length) {
      // For BTL searches, the API server will respond with all products regardless of the length (to support BTL being able to switch
      // product lengths), so when determining if there are matched products for affordability we need to re-run the length filter.
      // This is not true for resi, as the product form cannot trigger an affordability search and therefore when doing an affordability
      // search the product length filter will always be disabled and we only need to return products with a matching length.
      if (this.isBtlLendingType) {
        const [lowerPeriodBound, upperPeriodBound] = this.productsFilterHelper.getProductLengthBounds(
          this.model?.propertyAndLoan?.productLength ?? 0,
        );
        return lenderMatchedProducts.some(
          p => p.initialPeriod && p.initialPeriod >= lowerPeriodBound && p.initialPeriod <= upperPeriodBound,
        );
      }

      return true;
    }

    // If we loaded an outcome history, and that history specified that this lender had matching products at search
    // time, but there are no products for the lender, it means we've either not loaded products or one of their
    // products was never saved. We should still treat them as having matched products though.
    return this.productsSearchService.lendersWithMatchedProducts?.has(response.lenderName) ?? false;
  }

  public getLendersWithMatchedProducts(): string[] {
    return (
      this.igniteCommonDataService.results?.quotes
        ?.filter(q => !!this.getLenderMatchedProducts(q)?.products?.length)
        ?.map(q => q.lenderName) ?? []
    );
  }

  public getLenderAffordabilityData() {
    return (
      this.igniteCommonDataService.results?.quotes?.map(q => ({
        lenderName: q.lenderName,
        maximumAffordableLoanAmount: q.maximumAffordableLoanAmount,
        stressRate: q.stressRate,
      })) ?? []
    );
  }

  private get allowCriteriaPlus(): boolean {
    return this.authService.allowCriteriaPlus;
  }

  public get applicantOutcomeIds(): number[] | undefined {
    return this.model?.incomeAndExpenditure?.applicants?.flatMap((applicant: IUiApplicant) => this.getApplicantOutcomeIds(applicant)) ?? [];
  }

  public get applicantsAndOutcomeIds(): IApplicantOutcomeIds[] | undefined {
    return this.model?.incomeAndExpenditure?.applicants
      ?.map(
        (applicant: IUiApplicant) =>
          ({
            applicantNumber: applicant.applicantNumber,
            outcomeIds: this.getApplicantOutcomeIds(applicant),
          }) as IApplicantOutcomeIds,
      )
      .filter((applicant: IApplicantOutcomeIds) => applicant.outcomeIds.length > 0);
  }

  public canShowCriteriaFields(option: IUiBaseIncome, criteriaContexts: IBaseIncomeContext[]): boolean {
    return this.allowCriteriaPlus && criteriaContexts.some(context => context.category === option.category) && +option.amount > 0;
  }

  public get affordabilityRequest(): AffordabilityRequestUnion | null {
    return this.mappedModel(this.form.value);
  }

  public joinCriteriaResults(outcomesDetails: OutcomeResponse[]) {
    this.igniteCommonDataService.results$.update(result => {
      if (!result) {
        return null;
      }

      return {
        ...result,
        quotes: [
          ...result.quotes,
          ...outcomesDetails
            ?.filter(outcome => !this.igniteCommonDataService.results?.quotes.some(q => q.lenderName === outcome.overallResult.lender))
            .map(outcome => IgniteResponse.response(outcome)) ?? [],
        ],
      };
    });
  }

  public getResultsStart(): Observable<[AffordabilityPollResponse]> {
    const affordabilityRequest = this.affordabilityService.affordabilityModelRequest;
    this.miSaveOutcomeModel = null;
    const affordabilityAndUiRequest: AffordabilityAndUiRequest = {
      affordabilityRequest,
      uiModelJson: this.uiModelJson,
    };
    this.productFilterService.productsFilterContext = { repaymentMethod: affordabilityRequest?.repaymentMethod };
    this.productFilterService.setProductFilterDefinitions();
    this.affordabilityService.loading = true;
    this.criteriaV2Service.loading = true;
    this.form.markAsPristine();
    this.affordabilityService.affordabilityLoading$.next(true);

    return forkJoin([this.getAffordability(affordabilityAndUiRequest)]).pipe(
      tap(([results]) => {
        this.lenderCount = results.lenderCount;
        if (this.igniteCommonDataService.results) {
          const oldResultsLenders = this.igniteCommonDataService.results.lenders;
          this.igniteCommonDataService.results = results;
          const newResultsLendersMapName = results.lenders?.map(l => l.lenderName);
          oldResultsLenders?.forEach(lender => {
            if (lender.lenderName && !newResultsLendersMapName?.includes(lender.lenderName)) {
              this.igniteCommonDataService.results?.lenders?.push(lender as AffordabilityLenders);
            }
          });
        } else {
          this.igniteCommonDataService.results = results;
        }
        this.igniteCommonDataService.lenders = results.lenders || [];
      }),
    );
  }

  /** Runs `getCriteria()`, `getResultsStart()` and begins polling for results.
   * Returns a promise that resolves when all operations are complete. This promise returns the outcome
   * result (from `getCriteria`). */
  public async getResultsAndPoll(
    scheduled: boolean,
    rioForm: UntypedFormGroup | undefined = undefined,
    force: boolean = false,
  ) {
    try {
      const minLoadingBannerDurationPromise = new Promise(resolve => setTimeout(resolve, minimumLoadingBannerTime));

      this.affordabilityService.affordabilityModelRequest = this.mappedModel(
        this.form.valid ? this.form.getRawValue() : this.model,
        rioForm,
      );
      if (!this.affordabilityService.affordabilityModelRequest && this.model) {
        this.affordabilityService.affordabilityModelRequest = this.mappedModel(this.model, rioForm);
      }

      const criteriaPromise = firstValueFrom(
        this.criteriaV2Service.getCriteriaV2(this.propertySearchService, { scrollToTop: false }),
      ).then(x => x.details);
      criteriaPromise.finally(() => (this.criteriaV2Service.loading = false));

      if (this.igniteCommonDataService.results && this.form.pristine && !force) {
        return await criteriaPromise;
      }

      this.savedSearchCommonDataService.clearSavedSearch();

      // reset products before set new one
      this.productsSearchService.loading = true;
      this.productsSearchService.resetProducts();

      const res = await firstValueFrom(this.getResultsStart());

      const lenderProductsPromise = firstValueFrom(this.getMatchedLenderProducts(scheduled, res[0].affordabilitySearchId));
      lenderProductsPromise.then(() => (this.productsSearchService.loading = false));

      if (this.authService.hasRole(roles.lender)) {
        const auditModel: CreateLenderAuditLogRequest = {
          referenceId: this.igniteCommonDataService.affordabilitySearchId,
          lendingTypeId: this.lendingTypeService.lendingType?.id ?? -1,
          type: LenderAuditType.Ignite,
          result: LenderAuditResult.NotCompleted,
        };
        this.lenderAuditService.addLenderAudit(auditModel).subscribe();
      }

      // Start polling affordability, but don't block loading for it
      const pollAffordability$ = this.pollAffordability(res[0].pollJobID, res[0].affordabilitySearchId, criteriaPromise);

      // When affordability is finished, send the SSO callback (if required)
      pollAffordability$.pipe(filter(completed => completed)).subscribe(() => {
        if (this.s365Service.s365Data?.sourcingCallBack) {
          const hasAffordabilityEvents = this.s365Service.s365Data.events?.some(x => x.eventType === SsoCallBackEventType.Affordability);
          if (hasAffordabilityEvents) {
            const callbackRequest = this.s365Service.mapSmartr365CallbackRequest(this.affordabilityRequest);
            this.clubHubDataService.post<IgnitePlusAffordabilityCallbackRequest>('SmartrFitPlusCallback', callbackRequest).subscribe();
          }
        }
      });

      // We need to wait for at least one poll result to come back to populate the results object before hiding the
      // loading banner. We also need to wait for criteria and products, and wait for a minimum period for the advert.
      await Promise.allSettled([
        firstValueFrom(pollAffordability$),
        criteriaPromise,
        lenderProductsPromise,
        minLoadingBannerDurationPromise,
      ]);

      this.affordabilityService.loading = false;
      this.criteriaV2Service.loading = false;

      this.igniteCommonDataService.searchToolsInUse.affordability = true;
      this.igniteCommonDataService.searchToolsInUse.products = true;
      this.igniteCommonDataService.searchToolsInUse.criteria = true;

      return await criteriaPromise;
    } catch {
      this.affordabilityService.loading = false;
      this.criteriaV2Service.loading = false;
      this.criteriaV2Service.uiLoading = false;
      this.criteriaV2Service.dataLoading = false;
      return null;
    }
  }

  /**
   * Begins polling affordability. The returned Observable will omit a value every time a poll request completes, along
   * with a boolean indicating when it is completed.
   */
  private pollAffordability(
    pollJobID: number | null,
    affordabilitySearchId: string | null,
    criteriaPromise: Promise<OutcomeResponse[]>,
  ): Observable<boolean> {
    this.cancelAffordabilityPolling(false);
    const pollStop$ = this.affordabilityService.affordabilityPollStop$;
    const result$ = new Subject<boolean>();

    let shouldPollTimeout = false;
    const timeoutHandle = setTimeout(() => (shouldPollTimeout = true), maximumAffordabilityLoadingTime);

    let criteriaOutcomes: OutcomeResponse[] | undefined;
    criteriaPromise.then((res: OutcomeResponse[]) => {
      criteriaOutcomes = res;
      if (!this.authService.hasRole(roles.lender) && res) {
        this.joinCriteriaResults(res);
      }
    });

    const asyncLoop = async () => {
      while (true) {
        const pollRes = await firstValueFrom(
          this.getResultsPoll(pollJobID, affordabilitySearchId).pipe(takeUntil(pollStop$), defaultIfEmpty(null)),
        );

        if (!pollRes) {
          clearTimeout(timeoutHandle);
          result$.complete();
          return;
        }

        if (!this.authService.hasRole(roles.lender) && criteriaOutcomes) {
          this.joinCriteriaResults(criteriaOutcomes);
        }

        if (pollRes.completed || shouldPollTimeout) {
          clearTimeout(timeoutHandle);
          result$.next(true);
          result$.complete();
          this.affordabilityService.affordabilityLoading$.next(false);
          return;
        }

        result$.next(false);

        const waitResult = await firstValueFrom(timer(3000).pipe(takeUntil(pollStop$), defaultIfEmpty(null)));
        if (waitResult === null) {
          clearTimeout(timeoutHandle);
          result$.complete();
          return;
        }
      }
    };
    asyncLoop().catch(() => {
      clearTimeout(timeoutHandle);
      result$.complete();
      this.affordabilityService.affordabilityLoading$.next(false);
    });

    return result$;
  }

  private cancelAffordabilityPolling(resetLoadingState = true): void {
    this.affordabilityService.affordabilityPollStop$.next();
    this.affordabilityService.affordabilityPollStop$.complete();
    this.affordabilityService.affordabilityPollStop$ = new Subject<void>();

    if (resetLoadingState) {
      this.affordabilityService.affordabilityLoading$.next(false);
    }
  }

  private getResultsPoll(pollJobID: number | null, affordabilitySearchId: string | null): Observable<AffordabilityPollResponse> {
    if (!pollJobID || !affordabilitySearchId) {
      return of();
    }
    return this.getAffordabilityPoll(pollJobID, affordabilitySearchId).pipe(
      tap((results: AffordabilityPollResponse) => {
        this.progress = results.quotes.length;
        const lendersNotDone = this.igniteCommonDataService.lenders?.filter(l => !results.quotes.some(q => q.lenderName === l.lenderName));
        if (lendersNotDone) {
          lendersNotDone.forEach(lender => {
            results.quotes.push(AffordabilityPollResponse.response(lender));
          });
        }
        this.igniteCommonDataService.results = results;
      }),
    );
  }

  private getIntuitiveOutcomes(): Observable<OutcomeResponse[]> {
    return this.isBtlLendingType
      ? this.igniteBtlService.getIntuitiveOutcomes(this.affordabilityService.affordabilityModelRequest as BtlAffordabilityRequest)
      : this.clubHubDataService.post<OutcomeResponse[]>('Ignite/GetIntuitiveOutcomes', this.affordabilityService.affordabilityModelRequest);
  }

  private getCriteria(): Observable<[OutcomeResponse[], OutcomeDetails]> {
    this.criteriaV2Service.loading = true;
    return forkJoin([
      this.igniteCommonDataService.affordabilityOnly ? this.getIntuitiveOutcomes() : of([]),
      this.getCriteriaLenders(this.affordabilityService.affordabilityModelRequest),
    ]).pipe(
      tap(([intuitiveOutcomes, criteriaOutcomes]: [OutcomeResponse[], OutcomeDetails]) => {
        this.criteriaV2Service.criteriaSearchId = criteriaOutcomes.criteriaSearchId;
        this.criteriaV2Service.intuitiveContextCards = intuitiveOutcomes[0].outcomeContextCards ?? [];
        this.propertySearchService.splitOutPropertyCriteria(intuitiveOutcomes, criteriaOutcomes.details);
        this.criteriaV2Service.outcomeDetails.details = criteriaOutcomes.details;

        let contexts = criteriaOutcomes.contexts;
        if (contexts?.length > 0 && this.criteriaV2Service.intuitiveContextCards?.length > 0) {
          // remove duplicates
          contexts = contexts.filter(c => this.criteriaV2Service.intuitiveContextCards.every(ic => ic.answerId !== c.answerId));
        }
        this.criteriaV2Service.outcomeDetails.contexts = [...contexts, ...this.criteriaV2Service.intuitiveContextCards];
      }),
    );
  }

  /**
   * If an affordability model is present but hasn't yet been fetched, fetches it (implicitly fetching products).
   * Otherwise, if a product model is present but the products haven't yet been fetched, fetches them.
   */
  public async getAffordabilityAndProductsIfRequired() {
    if (this.affordabilityService.affordabilityModelRequest && !this.igniteCommonDataService.results) {
      if (this.lendingTypeService.lendingType) {
        this.criteriaV2Service.criteriaIssues = await firstValueFrom(
          this.criteriaV2Service.getIssues(this.lendingTypeService.lendingType.id),
        );
      }
      return this.getResultsAndPoll(false, this.rioService.rioForm);

    } else if (this.productsSearchService.productsModelRequest && !this.productsSearchService.matchedProducts) {
      this.productsSearchService.loading = true;
      if (this.lendingTypeService.lendingType) {
        this.criteriaV2Service.criteriaIssues = await firstValueFrom(
          this.criteriaV2Service.getIssues(this.lendingTypeService.lendingType.id),
        );
      }
      await firstValueFrom(
        this.productsSearchService.getProductsAndCriteria(false, null, this.productsSearchService.productsModelRequest),
      );
      this.productsSearchService.loading = false;
    }
  }

  public get productsSearchCompleted(): EventEmitter<void> {
    return this.productsSearchService.productsSearchCompleted;
  }

  public get getFixedInitialPeriod(): number | undefined {
    return this.fixedInitialPeriod;
  }

  public set setFixedInitialPeriod(period: number | undefined) {
    this.fixedInitialPeriod = period;
  }

  private mapAffordabilityToProductRequest(
    request: AffordabilityRequestUnion,
    affordabilitySearchId: string,
  ): ProductsResidentialModelRequest | ProductsBtlModelRequest | ProductsBdgModelRequest | undefined {
    if (request?.productType === LendingTypeCode.Res) {
      return ProductsResidentialModelRequest.mapAffordabilityToProductRequestResidential(
        request as AffordabilityRequest,
        affordabilitySearchId,
        this.s365Service.s365Data?.caseId ?? null,
      );
    } else if (request?.productType === LendingTypeCode.Btl) {
      return ProductsBtlModelRequest.mapAffordabilityToProductRequestBtl(
        request as BtlAffordabilityRequest,
        affordabilitySearchId,
        this.s365Service.s365Data?.caseId ?? null,
      );
    } else if (request.productType === LendingTypeCode.Bdg) {
      return ProductsBdgModelRequest.mapAffordabilityToProductRequestBdg(request as BdgAffordabilityRequest, affordabilitySearchId);
    }
  }

  private getMatchedLenderProducts(scheduled: boolean, affordabilitySearchId: string | null): Observable<MatchedLenderProducts[]> {
    const affordabilityRequest = this.affordabilityService.affordabilityModelRequest;
    const productsSearchRequestViewModel: ProductsResidentialModelRequest | ProductsBtlModelRequest | ProductsBdgModelRequest | undefined =
      affordabilityRequest && affordabilitySearchId
        ? this.mapAffordabilityToProductRequest(affordabilityRequest, affordabilitySearchId)
        : undefined;

    this.productsSearchService.loading = true;

    return this.rateTypesFromPreferences().pipe(
      switchMap(rateTypes => {
        // The affordability form has no Rate type field, so nothing competes with the preference here - unlike
        // Initial product period, which the mapper takes from the affordability request because the broker chose
        // it for this search. Seeded onto the request rather than the form: on this journey the products search
        // component never mounts, so the seeding it does on ngAfterViewInit never runs.
        if (productsSearchRequestViewModel && rateTypes.length && !productsSearchRequestViewModel.rateTypes?.length) {
          productsSearchRequestViewModel.rateTypes = rateTypes;
        }

        this.productsSearchService.productsModelRequest = productsSearchRequestViewModel;

        return this.productsSearchService.getProducts(scheduled, productsSearchRequestViewModel).pipe(
          tap(products => {
            if (productsSearchRequestViewModel) {
              this.matchedLenderProducts = products;
              this.productsSearchService.updateModelFromSF(productsSearchRequestViewModel);
            }
          }),
        );
      }),
    );
  }

  /**
   * The broker's saved Rate type preference, or an empty list when they have none.
   *
   * Never fails the search. A preference that cannot be read is a setting the broker does not get this time; a
   * products search that does not run is a broken journey.
   */
  private rateTypesFromPreferences(): Observable<ProductTypeString[]> {
    const lendingTypeCode = this.lendingTypeService.lendingType?.code as LendingTypeCode | undefined;

    if (!this.authService.isLoggedIn || !lendingTypeCode) {
      return of([]);
    }

    return this.searchFilterPreferencesService.getPreferences(lendingTypeCode).pipe(
      take(1),
      map(preferences => this.productPreferencesApplier.toSearchFormValues(preferences.productSearch).rateTypes),
      catchError(() => of([] as ProductTypeString[])),
    );
  }

  public setCriteriaResults(outcomes: OutcomeDetails, criteriaSearchId: string | null) {
    this.criteriaV2Service.criteriaSearchId = criteriaSearchId;
    this.propertySearchService.splitOutPropertyCriteria(null, outcomes.details);
  }

  public retryLender(lenderName: string): Observable<IgniteResponse> {
    if (!this.igniteCommonDataService.results) {
      throw new Error('No results to iterate');
    }
    const lenderIndex = this.igniteCommonDataService.results.quotes.findIndex(q => q.lenderName === lenderName);

    this.getCriteria();
    // this.getProducts(false);

    const affordabilityModel = this.mappedModel(this.model);
    if (affordabilityModel) {
      affordabilityModel.affordabilitySearchId = this.igniteCommonDataService.affordabilitySearchId;
      affordabilityModel.pollJobId = this.igniteCommonDataService.pollJobId;
    }

    return this.getAffordability(
      {
        affordabilityRequest: affordabilityModel,
        uiModelJson: this.uiModelJson,
      },
      lenderName,
    ).pipe(
      tap(result => {
        if (result) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          this.igniteCommonDataService.results!.quotes[lenderIndex] = result;
          const lenderProducts = this.productsSearchService.matchedProducts?.find(
            lenderProducts => lenderProducts.lenderName === result.lenderName,
          );
          if (lenderProducts) {
            this.productsSearchService.updateLenderAffordabilityMet(this.igniteCommonDataService.results?.quotes, lenderProducts);
            this.productsSearchService.updateResiProductMaxAffordableLoan(this.igniteCommonDataService.results?.quotes, lenderProducts);
          }
        }
      }),
    );
  }

  private getIntuitive(propertySearchRequestViewModel: PropertyModelRequest | null): Observable<OutcomeResponse[]> {
    return this.isBtlLendingType
      ? this.igniteBtlService.getIntuitive(propertySearchRequestViewModel)
      : this.clubHubDataService.post<OutcomeResponse[]>('propertyCriteriaV2/Intuitive', propertySearchRequestViewModel);
  }

  public async addIgniteCriteriaOutcomes(outcomeIds: (number | undefined)[], sendRequest = true) {
    this.criteriaV2Service.additionalOutcomeIds = [...new Set([...this.criteriaV2Service.additionalOutcomeIds, ...(outcomeIds ?? [])])];

    if (sendRequest) {
      // If results are null, user adding criteria from the 'would you like to add criteria'
      // popup after clicking get results
      // in this case, need to get results as normal.
      if (this.igniteCommonDataService.results === null) {
        const outcomes = await this.getResultsAndPoll(false);
        this.modalService.dismiss(outcomes);
      } else {
        // If results not null, adding criteria to existing fetched results so just
        // do getCriteria() without getting the entire results
        this.criteriaV2Service.loading = true;
        if (this.igniteCommonDataService.propertyOnly) {
          this.getCriteriaLenders(null).subscribe((criteriaOutcomes: OutcomeDetails) => {
            if (this.propertySearchService.propertySearchRequestViewModel) {
              this.propertySearchService.propertySearchRequestViewModel.propertySearchId = criteriaOutcomes.criteriaSearchId;
              this.propertySearchService.propertySearchRequestViewModel.caseId = this.s365Service.s365Data?.caseId ?? null;
            }
            this.propertySearchService.propertySearchId = criteriaOutcomes.criteriaSearchId;

            this.getIntuitive(this.propertySearchService.propertySearchRequestViewModel).subscribe(intuitiveOutcomes => {
              this.modalService.dismiss(criteriaOutcomes);
              this.affordabilityService.loading = false;
              this.criteriaV2Service.loading = false;
              this.propertySearchService.splitOutPropertyCriteria(intuitiveOutcomes, criteriaOutcomes.details);
              this.criteriaV2Service.outcomeDetails.details = criteriaOutcomes.details;
              this.criteriaV2Service.outcomeDetails.contexts = criteriaOutcomes.contexts;
            });
          });
        } else {
          this.getCriteria().subscribe(outcomes => {
            this.modalService.dismiss(outcomes);
            this.affordabilityService.loading = false;
            this.criteriaV2Service.loading = false;
          });
        }
      }
    }
  }

  public downloadSearchPdf(searchId: string | null, lenderName: string) {
    const folderName = `${searchId}/${lenderName.replace(/ /g, '').toLowerCase()}`;
    return this.clubHubDataService.getFile(`file/DownloadAsPdf?folder=${folderName}`, 'application/pdf');
  }

  public getPreviousOutcome(affordabilitySearchId: string): Observable<AffordabilityResult> {
    this.resetResults();
    this.currentStepName = StepName.Results;
    return this.clubHubDataService.get<AffordabilityResult>(`affordability/outcome/${affordabilitySearchId}`);
  }

  public addToSearchInUse(result: OutcomeHistoryResponse) {
    this.igniteCommonDataService.searchToolsInUse.affordability = !!result.affordability;
    this.igniteCommonDataService.searchToolsInUse.criteria = !!result.criteria || !!result.intuitiveOutcomes;
    this.igniteCommonDataService.searchToolsInUse.products = !!result.products;
    this.igniteCommonDataService.searchToolsInUse.property = !!result.property;
  }

  public saveMiDetails(model: MiSaveOutcomeModel): Observable<unknown> {
    return this.clubHubDataService.put<unknown>('outcome/setmidetails', model);
  }

  public getOutcomeIds(criteria: IUiCriteria | undefined): number[] {
    return (
      criteria?.questions
        .filter((x: IUiCriteriaQuestion) => x.answerId && x.answers.find(a => a.answerId === x.answerId)?.type === AnswerType.Outcome)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map((x: IUiCriteriaQuestion) => x.answerId!) ?? []
    );
  }

  public noUnsuccessfulCriteria(
    result: IgniteResponse,
    intuitiveOutcomes: OutcomeResponse[] | null = null,
    additionalOutcomes: OutcomeResponse[] | null = null,
  ) {
    const intuitiveResult = this.getIntuitiveOutcome(result.lenderName, intuitiveOutcomes)?.overallResult.outcomeResult;
    const additionalResult = this.getAdditionalOutcome(result.lenderName, additionalOutcomes)?.overallResult.outcomeResult;
    const overallOutcome = this.getOverallOutcome(intuitiveResult, additionalResult);

    return overallOutcome !== OutcomeResults.No;
  }

  public noUnsuccessfulCriteriaWithoutAffordability(lenderName: string) {
    const intuitiveResult = this.getIntuitiveOutcome(lenderName)?.overallResult.outcomeResult;
    const additionalResult = this.getAdditionalOutcome(lenderName)?.overallResult.outcomeResult;
    const overallOutcome = this.getOverallOutcome(intuitiveResult, additionalResult);

    return overallOutcome !== OutcomeResults.No;
  }

  public unsuccessfulProductsWithCriteria(lender: string) {
    return this.criteriaV2Service.getLenderOverallResultByLender(lender) === OutcomeResults.No;
  }

  public getOverallOutcome(...results: (OutcomeResults | string | undefined)[]) {
    return this.criteriaV2Service.getOverallOutcome(...results);
  }

  public getCriteriaFilterResult(result: IgniteResponse) {
    return this.getOverallOutcome(
      this.calculateOverallResult(this.getIntuitiveOutcome(result.lenderName)),
      this.calculateOverallResult(this.getAdditionalOutcome(result.lenderName)),
    );
  }

  public getPropertyFilterResult(result: IgniteResponse) {
    return this.getOverallOutcome(
      this.calculateOverallResult(this.getPropertyOutcome(result.lenderName)),
      this.calculateOverallResult(this.getPropertyIntuitiveOutcome(result.lenderName)),
    );
  }

  public initialModel(): IUiAffordabilityModel {
    return IUiAffordabilityModel.initialModel(this.initialApplicantAge, this.initialApplicant);
  }

  private getAffordability(model: AffordabilityAndUiRequest): Observable<AffordabilityPollResponse>;
  private getAffordability(model: AffordabilityAndUiRequest, lenderName?: string): Observable<IgniteResponse>;
  private getAffordability(
    model: AffordabilityAndUiRequest,
    lenderName?: string,
  ): Observable<IgniteResponse> | Observable<AffordabilityPollResponse> {
    if (this.mockAffordability) {
      if (lenderName) {
        return (
          of(mockAffordabilityResponse)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .pipe(map(lender => lender.quotes.find(l => l.lenderName === lenderName)!))
        );
      }

      return of(mockAffordabilityResponse);
    }

    if (lenderName) {
      return this.isBtlLendingType
        ? this.igniteBtlService.getAffordability(model, lenderName)
        : this.clubHubDataService.post<IgniteResponse>(`Affordability/${lenderName}`, model);
    }

    return this.isBtlLendingType
      ? this.igniteBtlService.getAffordability(model)
      : this.clubHubDataService.post<AffordabilityPollResponse>('Affordability', model);
  }

  public getAffordabilityPoll(pollJobID: number, affordabilitySearchId: string): Observable<AffordabilityPollResponse> {
    if (this.mockAffordability) {
      return of(mockAffordabilityResponse);
    }

    return this.isBtlLendingType
      ? this.igniteBtlService.getAffordabilityPoll(pollJobID, affordabilitySearchId)
      : this.clubHubDataService.get<AffordabilityPollResponse>(
        `Affordability/Poll/${pollJobID}/${affordabilitySearchId}?productType=${this.lendingTypeCode}`,
      );
  }

  private isBtlModelRequest(model: AffordabilityRequestUnion | null) {
    return model?.productType === LendingTypeCode.Btl;
  }

  public get isBtlLendingType() {
    return this.lendingTypeCode?.toUpperCase() === LendingTypeCode.Btl;
  }

  private getBtlLenders(requestModel: any): Observable<OutcomeDetails> {
    return this.igniteBtlService.getLenders(requestModel);
  }

  private getStandardLenders(requestModel: any): Observable<OutcomeDetails> {
    return this.clubHubDataService.post<OutcomeDetails>('Ignite/GetLenders', requestModel);
  }

  private getCriteriaLenders(
    model: AffordabilityRequestUnion | null,
  ): Observable<OutcomeDetails> {
    const allOutcomeIds = [
      ...(model?.outcomeIds ?? []),
      ...(this.applicantOutcomeIds ? this.applicantOutcomeIds : []),
      ...this.basketService.answerIds,
    ];
    const distinctIds = [...new Set(allOutcomeIds)];

    const criteriaOnlyAffordabilitySearchId = this.igniteCommonDataService.criteriaOnly
      ? (this.igniteCommonDataService.results as CriteriaResponse).criteriaSearchId
      : model?.affordabilitySearchId;

    const getLendersRequestModel = {
      outcomeIds: distinctIds,
      appName: this.igniteCommonDataService.propertyOnly ? appName.propertyCriteria : appName.criteria,
      affordabilitySearchId: this.igniteCommonDataService.propertyOnly
        ? null
        : criteriaOnlyAffordabilitySearchId,
      autoAddedOutcomeIds: this.propertySearchService.autoAddedPropertyOutcomeIds,
    };

    if (allOutcomeIds.length === 0) {
      return of({
        dateTime: '',
        contexts: [],
        details: [],
        lendingType: '',
        autoAddedOutcomeIds: [],
        criteriaSearchId: null,
      });
    }

    return this.isBtlModelRequest(model)
      ? this.getBtlLenders(getLendersRequestModel)
      : this.getStandardLenders(getLendersRequestModel);
  }

  private getAllOutcomeIds(): number[] {
    if (!this.model) {
      return [];
    }
    const { propertyAndLoan } = this.model;

    const outcomeIds: IUiCriteria[] = [];

    if (propertyAndLoan?.capitalRaisingCriteria && propertyAndLoan.capitalRaising?.value && propertyAndLoan.capitalRaising?.showCriteria) {
      outcomeIds.push(propertyAndLoan.capitalRaisingCriteria);
    }
    if (
      propertyAndLoan?.repaymentMethodInterestOnlyCriteria &&
      propertyAndLoan.repaymentMethod?.value === RepaymentMethod.InterestOnly &&
      propertyAndLoan.repaymentMethod?.showInterestOnlyCriteria
    ) {
      outcomeIds.push(propertyAndLoan.repaymentMethodInterestOnlyCriteria);
    }
    if (
      propertyAndLoan?.repaymentMethodInterestOnlyPartAndPartCriteria &&
      propertyAndLoan.repaymentMethod?.value === RepaymentMethod.InterestOnlyPartAndPart &&
      propertyAndLoan.repaymentMethod?.showInterestOnlyPartAndPartCriteria
    ) {
      outcomeIds.push(propertyAndLoan.repaymentMethodInterestOnlyPartAndPartCriteria);
    }

    return outcomeIds.flatMap((criteria: IUiCriteria) => this.getOutcomeIds(criteria));
  }

  private renumberApplicants(model: IUiAffordabilityModel): void {
    model?.applicants?.applicantAges?.forEach((applicant, index) => {
      applicant.applicantNumber = index + 1;
    });
    model?.incomeAndExpenditure?.applicants?.forEach((applicant, index) => {
      applicant.applicantNumber = index + 1;
    });
  }

  private applyUiModel(model: IUiAffordabilityModel | undefined): void {
    this.model = model;
    this.igniteCommonDataService.affordabilityModel = this.model ?? null;
    this.affordabilityService.affordabilityModelRequest = this.mappedModel(this.model);
    if (this.lendingTypeCode === LendingTypeCode.Res) {
      this.rioService.reset(this.model?.applicants?.numberOfApplicants, this.model);
    }
    this.form.reset(this.model);
  }

  public setIgniteModel(uiModel: IUiAffordabilityModel) {
    this.updateModel(uiModel);
    this.igniteCommonDataService.affordabilityModel = uiModel;
    this.affordabilityService.affordabilityModelRequest = this.mappedModel(uiModel);
  }

  public mappedModel(
    uiModel: IUiAffordabilityModel | undefined,
    rioForm: UntypedFormGroup | null = null,
    ignoreServiceUpdated: boolean = false,
  ): AffordabilityRequestUnion | null {
    if (!isEmpty(uiModel)) {
      switch (this.lendingTypeCode.toUpperCase()) {
        case LendingTypeCode.Res:
          if (!ignoreServiceUpdated) {
            this.setFixedInitialPeriod = uiModel.propertyAndLoan?.productLength ?? 0;
          }
          return AffordabilityRequest.mappedModelResi(
            uiModel,
            rioForm,
            newGuid(),
            this.getAllOutcomeIds(),
            this.mapCurrentMortgageLender(uiModel.propertyAndLoan),
            this.helperService.getDateOrNull,
            this.mapMainIncome.bind(this),
            this.s365Service.s365Data?.caseId ?? null,
          );
        case LendingTypeCode.Btl:
          if (!ignoreServiceUpdated) {
            this.setFixedInitialPeriod = uiModel.propertyAndLoan?.productLength ?? 0;
          }
          return BtlAffordabilityRequest.mappedModelBtl(
            uiModel,
            newGuid(),
            this.getAllOutcomeIds(),
            this.mapCurrentMortgageLender(uiModel.propertyAndLoan),
            this.helperService.getDateOrNull,
            this.mapMainIncome.bind(this),
            this.s365Service.s365Data?.caseId ?? null,
          );
        case LendingTypeCode.Bdg:
          return {
            regulated: true,
          } as BdgAffordabilityRequest;
        default:
          return null;
      }
    }
    return null;
  }

  private mapCurrentMortgageLender(propertyAndLoanStep: IPropertyAndLoan | undefined) {
    return this.helperService.getCurrentMortgageLender(propertyAndLoanStep?.currentMortgageLender, propertyAndLoanStep?.mortgageLender);
  }

  private get uiModelJson(): string {
    const uiModelObj = this.form.getRawValue();
    if (this.lendingTypeCode === LendingTypeCode.Res) {
      uiModelObj.rioApplicants = this.rioService.rioForm?.getRawValue()?.applicants;
    }
    return JSON.stringify(uiModelObj);
  }

  private get initialApplicantAge(): IUiApplicantAge {
    return IUiApplicantAge.initialApplicantAge();
  }

  private get initialApplicant(): IUiApplicant {
    return IUiApplicant.initialApplicant();
  }

  private get initialPropertyBtl(): IPropertyBtl {
    return IPropertyBtl.initialPropertyBtl();
  }

  private get initialPropertyResi(): IUiOtherMortgage {
    return IUiOtherMortgage.initialPropertyResi();
  }

  private mapMainIncome(mainIncome: IUiMainIncome, affordabilityType: LendingTypeCode): MainIncome {
    if (mainIncome.grossSalaryForPreviousPeriod) {
      this.formStateService.hideGrossSalaryPreviousFields = false;
    }
    if (mainIncome.grossDividendsForPreviousPeriod) {
      this.formStateService.hideGrossDividendsPreviousFields = false;
    }
    if (mainIncome.netProfitBeforeDividendsForPreviousPeriod) {
      this.formStateService.hideNetProfitBeforeDividendsPreviousFields = false;
    }
    return MainIncome.mapMainIncome(mainIncome, affordabilityType);
  }

  private getApplicantOutcomeIds(applicant: IUiApplicant): number[] {
    const workAdditionalIncomesOutcomeIds =
      applicant.workAdditionalIncome
        ?.filter(
          (x: UiAdditionalIncome) => this.canShowCriteriaFields(x.option, workRelatedIncomeCriteria) && x.option.showCriteria && x.criteria,
        )
        .flatMap((x: UiAdditionalIncome) => this.getOutcomeIds(x.criteria)) ?? [];

    const benefitAdditionalIncomesOutcomeIds =
      applicant.benefitAdditionalIncome
        ?.filter(
          (x: UiAdditionalIncome) => this.canShowCriteriaFields(x.option, benefitIncomeCriteria) && x.option.showCriteria && x.criteria,
        )
        .flatMap((x: UiAdditionalIncome) => this.getOutcomeIds(x.criteria)) ?? [];

    const otherAdditionalIncomeOutcomeIds =
      applicant.otherAdditionalIncome
        ?.filter(
          (x: UiAdditionalIncome) => this.canShowCriteriaFields(x.option, otherIncomeCriteria) && x.option.showCriteria && x.criteria,
        )
        .flatMap((x: UiAdditionalIncome) => this.getOutcomeIds(x.criteria)) ?? [];

    return [...workAdditionalIncomesOutcomeIds, ...benefitAdditionalIncomesOutcomeIds, ...otherAdditionalIncomeOutcomeIds];
  }

  public getLoanAmount() {
    return this.affordabilityService.affordabilityModelRequest?.loanAmount;
  }

  public getMortgageTerm(): string {
    const mortgageTerm: number | undefined = this.affordabilityService.affordabilityModelRequest?.mortgageTermYears;
    if (mortgageTerm) {
      const length: string = mortgageTerm > 1 ? 'years' : 'year';
      return `${mortgageTerm} ${length}`;
    }

    return '';
  }

  public getRepaymentMethod(): string {
    return repaymentMethodToString(this.affordabilityService.affordabilityModelRequest?.repaymentMethod);
  }

  public async gotoOutcomes() {
    this.modalService.close();
    this.currentStepName = StepName.Results;
    this.igniteCommonDataService.searchToolsInUse.criteria = true;
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 200);
    this.analyticsService.log('Ignite Results', 'L&G');

    if (this.igniteCommonDataService.results === null) {
      const outcomes = await this.getResultsAndPoll(false);
      this.modalService.dismiss(outcomes);
    }
    this.criteriaV2Service.getCriteriaV2(this.propertySearchService).subscribe({
      next: () => {
        this.affordabilityService.loading = false;
        this.criteriaV2Service.loading = false;
        this.setProductFiltersFromCriteria();
      },
      error: () => {
        this.affordabilityService.loading = false;
        this.criteriaV2Service.loading = false;
        this.criteriaV2Service.uiLoading = false;
        this.criteriaV2Service.dataLoading = false;
      },
    });
  }

  public set auditResultsStatusChange(status: AuditResultsStatus) {
    this.auditResultsStatus = status;
  }

  public get auditResultsCurrentStatus() {
    return this.auditResultsStatus;
  }

  public prePopulateFromS365Data(model: IUiAffordabilityModel | undefined): void {
    this.form = new UntypedFormGroup({});
    this.applyUiModel(model);
  }

  public setProductFiltersFromCriteria() {
    const contexts = this.criteriaV2Service.basketItems;
    contexts.forEach(context => {
      const filterName = this.productFilterService.mapCriteriaToFilters(context.criteriaContextName);
      if (filterName) {
        this.productFilterService.setFilter('Tailored products', filterName, true);
      }
    });
  }

  public calculateOverallResult(outcome: OutcomeResponse | undefined): OutcomeResults | undefined {
    const outcomes = outcome?.contextResults.map(o => o.outcomeResult);
    if (!outcomes?.length) {
      return undefined;
    }

    if (!outcomes || outcomes.includes(OutcomeResults.Unset)) {
      return OutcomeResults.Unset;
    } else if (outcomes.includes(OutcomeResults.No)) {
      return OutcomeResults.No;
    } else if (outcomes.includes(OutcomeResults.Refer)) {
      return OutcomeResults.Refer;
    }

    return OutcomeResults.Yes;
  }

  public calculateOverallVerified(outcome: OutcomeResponse | undefined) {
    const verifications = outcome?.contextResults.map(o => o.verified);
    if (!verifications?.length) {
      return false;
    }
    if (verifications.includes(false)) {
      return false;
    }

    return true;
  }

  public get hasBetaFeaturesRole(): boolean {
    return this.authService.hasRole(roles.betaFeaturesClubHub);
  }

  public get isLoggedIn() {
    return this.authService.isLoggedIn;
  }

  public get isProductsOnly(): boolean {
    return this.igniteCommonDataService.searchToolsInUse.products && !this.igniteCommonDataService.searchToolsInUse.affordability;
  }

  public get preStandardSearchChanges(): boolean {
    if (this.isProductsOnly) {
      const missingProductTypeExtended =
        !!this.productsSearchService.model && !this.productsSearchService.model.mortgageRequirements.productTypeExtended;

      const isHistoricRightToBuySearch =
        !!this.productsSearchService.model &&
        this.productsSearchService.model.mortgageRequirements.productTypeExtended === ProductType.RightToBuy &&
        this.productsSearchService.model.mortgageRequirements.mortgageType === MortgageType.Purchase &&
        !this.productsSearchService.model.mortgageRequirements.discountedPurchasePrice;

      return missingProductTypeExtended || isHistoricRightToBuySearch;
    }

    //Don't allow hitorical Right To Buy searches
    if (this.isProductsOnly) {
      return (
        !!this.productsSearchService.model &&
        this.productsSearchService.model.mortgageRequirements.productTypeExtended === ProductType.RightToBuy &&
        !this.productsSearchService.model.mortgageRequirements.discountedPurchasePrice
      );
    }

    // btl affordability doesn't have a productTypeExtended field
    return !!this.model && !this.model.propertyAndLoan?.productTypeExtended && this.lendingTypeCode.toUpperCase() !== LendingTypeCode.Btl;
  }

  public get showReRunCaseModal() {
    if (this.isProductsOnly && this.lendingTypeCode.toUpperCase() === LendingTypeCode.Bdg) {
      return false;
    }

    return this.preStandardSearchChanges;
  }
}
